/**
 * academyView.ts - Academy view renderer
 *
 * Shows ghost training academy with 7 training types.
 * Each ghost can train each type up to level 5.
 * Background: academy-bg.webp for classroom immersion.
 */

import type { GameStateData, GhostInstance } from '../game/state.js';
import { GHOST_DEFS } from '../game/data/ghosts.js';
import {
  ACADEMY_TRAINING_DEFS,
  ALL_ACADEMY_TRAINING_IDS,
  academyTrainingCost,
  academyTrainingDuration,
} from '../game/data/academyTrainings.js';
import type { AcademyTrainingId } from '../game/data/academyTrainings.js';
import { t } from '../i18n/index.js';
import { formatSp } from './formatters.js';

export type AcademyAction =
  | { action: 'start_training'; ghostInstanceId: string; trainingId: AcademyTrainingId }
  | { action: 'complete_training'; ghostInstanceId: string }
  | { action: 'skip_training'; ghostInstanceId: string };

export type AcademyCallback = (ev: AcademyAction) => void;

/** Currently selected ghost in the academy */
let selectedGhostId: string | null = null;

export function setSelectedAcademyGhost(ghostId: string | null): void {
  selectedGhostId = ghostId;
}

export function renderAcademyView(
  container: HTMLElement,
  state: GameStateData,
  onAction: AcademyCallback,
): void {
  container.innerHTML = '';

  // === Classroom banner image ===
  const bannerImg = document.createElement('img');
  bannerImg.className = 'academy-classroom-banner';
  bannerImg.src = '/images/views/academy-bg.webp';
  bannerImg.alt = '';
  bannerImg.draggable = false;
  container.appendChild(bannerImg);

  const title = document.createElement('div');
  title.className = 'academy-title';
  title.textContent = t('academy_title');
  container.appendChild(title);

  // Show ghosts currently training in the classroom
  const trainingGhosts = state.ghosts.filter((g) => g.academySession !== null);
  if (trainingGhosts.length > 0) {
    for (const ghost of trainingGhosts) {
      const ghostDef = GHOST_DEFS[ghost.characterId];
      if (!ghostDef || !ghost.academySession) continue;

      const trainingDef = ACADEMY_TRAINING_DEFS[ghost.academySession.trainingId];
      const now = Date.now();
      const isComplete = now >= ghost.academySession.completesAt;

      const indicator = document.createElement('div');
      indicator.className = 'academy-training-ghost-indicator';

      const avatar = document.createElement('img');
      avatar.className = 'academy-training-ghost-avatar';
      avatar.src = ghostDef.image;
      avatar.alt = t(ghostDef.nameKey as Parameters<typeof t>[0]);
      avatar.draggable = false;
      indicator.appendChild(avatar);

      const info = document.createElement('div');
      info.className = 'academy-training-ghost-info';
      info.textContent = `${t(ghostDef.nameKey as Parameters<typeof t>[0])} - ${t(trainingDef.nameKey as Parameters<typeof t>[0])} ${isComplete ? t('academy_complete') : t('academy_in_progress')}`;
      indicator.appendChild(info);

      container.appendChild(indicator);
    }
  }

  // Filter ghosts available for academy (idle or already in academy)
  const academyGhosts = state.ghosts.filter((g) =>
    (!g.assignedRoomId && !g.training && !g.assignedFacilityId && !state.lobbyStaff.includes(g.id))
    || g.academySession !== null,
  );

  if (academyGhosts.length === 0 && state.ghosts.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'academy-empty';
    empty.textContent = t('academy_empty');
    container.appendChild(empty);
    return;
  }

  // Ghost selector
  const selectorTitle = document.createElement('div');
  selectorTitle.className = 'academy-section-title';
  selectorTitle.textContent = t('academy_select_ghost');
  container.appendChild(selectorTitle);

  const ghostList = document.createElement('div');
  ghostList.className = 'academy-ghost-list';

  for (const ghost of state.ghosts) {
    const def = GHOST_DEFS[ghost.characterId];
    if (!def) continue;

    const isAvailable = academyGhosts.includes(ghost);
    const isSelected = selectedGhostId === ghost.id;

    const ghostCard = document.createElement('div');
    ghostCard.className = 'academy-ghost-card';
    if (isSelected) ghostCard.classList.add('selected');
    if (!isAvailable) ghostCard.classList.add('unavailable');
    if (ghost.academySession) ghostCard.classList.add('in-training');

    const avatar = document.createElement('img');
    avatar.className = 'academy-ghost-avatar';
    avatar.src = def.image;
    avatar.alt = t(def.nameKey as Parameters<typeof t>[0]);
    avatar.draggable = false;
    ghostCard.appendChild(avatar);

    const nameEl = document.createElement('div');
    nameEl.className = 'academy-ghost-name';
    nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    ghostCard.appendChild(nameEl);

    const statusEl = document.createElement('div');
    statusEl.className = 'academy-ghost-status';
    if (ghost.academySession) {
      const now = Date.now();
      const isComplete = now >= ghost.academySession.completesAt;
      statusEl.textContent = isComplete ? t('academy_complete') : t('academy_in_progress');
    } else if (!isAvailable) {
      statusEl.textContent = t('academy_ghost_busy');
    } else {
      statusEl.textContent = t('ghost_level', { n: ghost.level });
    }
    ghostCard.appendChild(statusEl);

    if (isAvailable) {
      ghostCard.addEventListener('click', () => {
        selectedGhostId = ghost.id;
        renderAcademyView(container, state, onAction);
      });
    }

    ghostList.appendChild(ghostCard);
  }

  container.appendChild(ghostList);

  // If a ghost is selected, show training options
  if (selectedGhostId) {
    const ghost = state.ghosts.find((g) => g.id === selectedGhostId);
    if (ghost) {
      renderGhostTrainingPanel(container, ghost, state, onAction);
    }
  }
}

function renderGhostTrainingPanel(
  container: HTMLElement,
  ghost: GhostInstance,
  state: GameStateData,
  onAction: AcademyCallback,
): void {
  const def = GHOST_DEFS[ghost.characterId];
  if (!def) return;

  const panel = document.createElement('div');
  panel.className = 'academy-training-panel';

  // Ghost info header
  const headerEl = document.createElement('div');
  headerEl.className = 'academy-panel-header';
  headerEl.textContent = `${t(def.nameKey as Parameters<typeof t>[0])} - ${t('ghost_level', { n: ghost.level })}`;
  panel.appendChild(headerEl);

  // If currently training in academy
  if (ghost.academySession) {
    const session = ghost.academySession;
    const trainingDef = ACADEMY_TRAINING_DEFS[session.trainingId];
    const now = Date.now();
    const isComplete = now >= session.completesAt;

    const trainingStatus = document.createElement('div');
    trainingStatus.className = 'academy-current-training';

    const trainingName = document.createElement('div');
    trainingName.className = 'academy-current-name';
    trainingName.textContent = t(trainingDef.nameKey as Parameters<typeof t>[0]);
    trainingStatus.appendChild(trainingName);

    if (isComplete) {
      const completeBtn = document.createElement('button');
      completeBtn.className = 'academy-complete-btn';
      completeBtn.textContent = t('academy_complete');
      completeBtn.addEventListener('click', () =>
        onAction({ action: 'complete_training', ghostInstanceId: ghost.id }),
      );
      trainingStatus.appendChild(completeBtn);
    } else {
      const remaining = Math.ceil((session.completesAt - now) / 1000);
      const total = session.completesAt - session.startedAt;
      const elapsed = Math.min(now - session.startedAt, total);
      const pct = Math.round((elapsed / total) * 100);

      const progressBar = document.createElement('div');
      progressBar.className = 'academy-progress-bar';
      const progressFill = document.createElement('div');
      progressFill.className = 'academy-progress-fill';
      progressFill.style.width = `${pct}%`;
      progressBar.appendChild(progressFill);
      trainingStatus.appendChild(progressBar);

      const timeEl = document.createElement('div');
      timeEl.className = 'academy-time-remaining';
      timeEl.textContent = `${remaining}s`;
      trainingStatus.appendChild(timeEl);

      const skipBtn = document.createElement('button');
      skipBtn.className = 'academy-skip-btn';
      skipBtn.textContent = t('academy_skip_ad');
      skipBtn.addEventListener('click', () =>
        onAction({ action: 'skip_training', ghostInstanceId: ghost.id }),
      );
      trainingStatus.appendChild(skipBtn);
    }

    panel.appendChild(trainingStatus);
  } else {
    // Training course list
    const courseTitle = document.createElement('div');
    courseTitle.className = 'academy-course-title';
    courseTitle.textContent = t('academy_select_training');
    panel.appendChild(courseTitle);

    const courseList = document.createElement('div');
    courseList.className = 'academy-course-list';

    for (const trainingId of ALL_ACADEMY_TRAINING_IDS) {
      const trainingDef = ACADEMY_TRAINING_DEFS[trainingId];
      const currentLevel = ghost.academySkills[trainingId];
      const isMaxed = currentLevel >= trainingDef.maxLevel;

      const courseCard = document.createElement('div');
      courseCard.className = 'academy-course-card';
      if (isMaxed) courseCard.classList.add('maxed');

      const courseHeader = document.createElement('div');
      courseHeader.className = 'academy-course-header';

      const courseName = document.createElement('div');
      courseName.className = 'academy-course-name';
      courseName.textContent = t(trainingDef.nameKey as Parameters<typeof t>[0]);
      courseHeader.appendChild(courseName);

      const levelBadge = document.createElement('div');
      levelBadge.className = 'academy-level-badge';
      levelBadge.textContent = isMaxed
        ? t('academy_skill_maxed')
        : t('academy_skill_level', { n: currentLevel, max: trainingDef.maxLevel });
      courseHeader.appendChild(levelBadge);

      courseCard.appendChild(courseHeader);

      const courseDesc = document.createElement('div');
      courseDesc.className = 'academy-course-desc';
      courseDesc.textContent = t(trainingDef.descKey as Parameters<typeof t>[0]);
      courseCard.appendChild(courseDesc);

      const courseEffect = document.createElement('div');
      courseEffect.className = 'academy-course-effect';
      courseEffect.textContent = t(trainingDef.effectKey as Parameters<typeof t>[0]);
      courseCard.appendChild(courseEffect);

      if (!isMaxed) {
        const cost = academyTrainingCost(trainingId, currentLevel);
        const duration = academyTrainingDuration(trainingId, currentLevel);

        const courseActions = document.createElement('div');
        courseActions.className = 'academy-course-actions';

        const costInfo = document.createElement('span');
        costInfo.className = 'academy-course-cost';
        costInfo.textContent = `${formatSp(cost)} SP | ${Math.round(duration / 1000)}s`;
        courseActions.appendChild(costInfo);

        const startBtn = document.createElement('button');
        startBtn.className = 'academy-start-btn';
        startBtn.disabled = state.sp < cost;
        startBtn.textContent = t('training_select');
        startBtn.addEventListener('click', () =>
          onAction({
            action: 'start_training',
            ghostInstanceId: ghost.id,
            trainingId,
          }),
        );
        courseActions.appendChild(startBtn);

        courseCard.appendChild(courseActions);
      }

      courseList.appendChild(courseCard);
    }

    panel.appendChild(courseList);
  }

  container.appendChild(panel);
}
